import type { Transaction } from "@rocicorp/zero";
import {
  dropConnectorUsage,
  recordConnectorUsage,
  usageRef,
} from "./connectorUsages";
import { zql } from "./generatedDrizzleSchema";
import { slugifyName } from "./slug";

// Derived from the connector's name, not its identifier: the slug is the only
// identity that survives in step configs after the connector is deleted, and
// generic identifiers (api_key) carry none.
function generateSlug(
  identifier: string,
  name: string,
  existingSlugs: Set<string>
): string {
  const base = slugifyName(name) || slugifyName(identifier);
  if (!existingSlugs.has(base)) return base;

  const withIdentifier = slugifyName(`${identifier}-${name}`);
  if (withIdentifier !== base && !existingSlugs.has(withIdentifier))
    return withIdentifier;

  let i = 2;
  while (existingSlugs.has(`${base}-${i}`)) i++;
  return `${base}-${i}`;
}

export async function getWorkflowIdForCommit(
  tx: Pick<Transaction, "run">,
  commitId: string
): Promise<string> {
  const [commit] = await tx.run(zql.workflowCommits.where("id", commitId));
  if (!commit) throw new Error("Commit not found");
  const [branch] = await tx.run(
    zql.workflowBranches.where("id", commit.branchId)
  );
  if (!branch) throw new Error("Branch not found");
  return branch.workflowId;
}

export async function ensureSlug(
  tx: Transaction,
  workflowId: string,
  connectedAppId: string,
  identifier: string,
  name: string,
  tenantId: string
): Promise<string> {
  const inWorkflow = await tx.run(
    zql.connectedAppUsages
      .where("type", "workflow_slug")
      .where("refId", workflowId)
  );
  const existing = inWorkflow.find((r) => r.connectedAppId === connectedAppId);
  if (existing) return existing.subKey;

  const taken = new Set(inWorkflow.map((r) => r.subKey));
  const slug = generateSlug(identifier, name, taken);
  await recordConnectorUsage(
    tx,
    connectedAppId,
    usageRef.workflowSlug(workflowId, slug),
    tenantId
  );
  return slug;
}

// Back an existing (orphaned) reference slug with a connector, rather than
// generating a fresh one. Used to reconnect a step's dangling app reference
// without appending a duplicate config entry. Re-points a stale mapping (one
// that resolves to a no-longer-existent app) to the given app.
export async function claimSlug(
  tx: Transaction,
  workflowId: string,
  slug: string,
  connectedAppId: string,
  tenantId: string
): Promise<string> {
  const existing = await resolveSlug(tx, workflowId, slug);
  if (existing === connectedAppId) return slug;
  if (existing) {
    await dropConnectorUsage(
      tx,
      existing,
      usageRef.workflowSlug(workflowId, slug)
    );
  }
  await recordConnectorUsage(
    tx,
    connectedAppId,
    usageRef.workflowSlug(workflowId, slug),
    tenantId
  );
  return slug;
}

export async function resolveSlug(
  tx: Pick<Transaction, "run">,
  workflowId: string,
  slug: string
): Promise<string | null> {
  const [row] = await tx.run(
    zql.connectedAppUsages
      .where("type", "workflow_slug")
      .where("refId", workflowId)
      .where("subKey", slug)
  );
  return row?.connectedAppId ?? null;
}

export async function slugForApp(
  tx: Pick<Transaction, "run">,
  workflowId: string,
  connectedAppId: string
): Promise<string | undefined> {
  const [row] = await tx.run(
    zql.connectedAppUsages
      .where("type", "workflow_slug")
      .where("refId", workflowId)
      .where("connectedAppId", connectedAppId)
  );
  return row?.subKey;
}

// A workflow_slug mapping outlives its last step, so drop it once no commit_step
// in the workflow still references the connector.
export async function releaseWorkflowSlugIfUnused(
  tx: Transaction,
  workflowId: string,
  connectedAppId: string,
  slug: string
): Promise<void> {
  const stillUsed = await tx.run(
    zql.connectedAppUsages
      .where("type", "commit_step")
      .where("connectedAppId", connectedAppId)
      .whereExists("commit", (c) =>
        c.whereExists("branch", (b) => b.where("workflowId", workflowId))
      )
      .limit(1)
  );
  if (stillUsed.length === 0) {
    await dropConnectorUsage(
      tx,
      connectedAppId,
      usageRef.workflowSlug(workflowId, slug)
    );
  }
}
